import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  Loader2,
  ArrowRight,
  Users,
  Car,
  RefreshCw,
  ArrowLeft,
  Download,
  FileText,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import BoyceImportTab from '@/components/data-import/BoyceImportTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// System fields for customer mapping
const CUSTOMER_FIELDS = [
  { value: 'skip', label: '-- Skip this column --' },
  { value: 'display_name', label: 'Display Name (Full Name)' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
];

// System fields for vehicle mapping
const VEHICLE_FIELDS = [
  { value: 'skip', label: '-- Skip this column --' },
  { value: 'rego', label: 'Registration Number (Required)' },
  { value: 'make', label: 'Make (e.g., Toyota)' },
  { value: 'model', label: 'Model (e.g., Camry)' },
  { value: 'year', label: 'Year' },
  { value: 'color', label: 'Color' },
  { value: 'vin', label: 'VIN Number' },
  { value: 'engine_number', label: 'Engine Number' },
  { value: 'odometer', label: 'Odometer (km)' },
  { value: 'customer_email', label: 'Customer Email (for linking)' },
  { value: 'customer_phone', label: 'Customer Phone (for linking)' },
  { value: 'customer_name', label: 'Customer Name (for linking)' },
  { value: 'notes', label: 'Notes' },
];

// System fields for invoice mapping
const INVOICE_FIELDS = [
  { value: 'skip', label: '-- Skip this column --' },
  { value: 'original_invoice_no', label: 'Invoice Number (Original)' },
  { value: 'invoice_date', label: 'Invoice Date' },
  { value: 'vehicle_rego', label: 'Vehicle Rego (for linking)' },
  { value: 'customer_email', label: 'Customer Email (for linking)' },
  { value: 'customer_phone', label: 'Customer Phone (for linking)' },
  { value: 'customer_name', label: 'Customer Name (for linking)' },
  { value: 'subtotal', label: 'Subtotal (before GST)' },
  { value: 'gst', label: 'GST Amount' },
  { value: 'total', label: 'Total Amount' },
  { value: 'discount', label: 'Discount Amount' },
  { value: 'status', label: 'Status (paid/unpaid)' },
  { value: 'line_item_description', label: 'Service/Work Description' },
  { value: 'notes', label: 'Notes' },
  { value: 'service_notes', label: 'Service Notes' },
  { value: 'odometer', label: 'Odometer Reading' },
  { value: 'payment_method', label: 'Payment Method' },
];

// Auto-mapping suggestions
const AUTO_MAP_CUSTOMER = {
  'name': 'display_name',
  'full name': 'display_name',
  'customer name': 'display_name',
  'customer': 'display_name',
  'first name': 'first_name',
  'firstname': 'first_name',
  'last name': 'last_name',
  'lastname': 'last_name',
  'surname': 'last_name',
  'email': 'email',
  'e-mail': 'email',
  'phone': 'phone',
  'mobile': 'phone',
  'telephone': 'phone',
  'contact': 'phone',
  'address': 'address',
  'street': 'address',
};

const AUTO_MAP_VEHICLE = {
  'rego': 'rego',
  'registration': 'rego',
  'reg': 'rego',
  'plate': 'rego',
  'number plate': 'rego',
  'license plate': 'rego',
  'make': 'make',
  'manufacturer': 'make',
  'brand': 'make',
  'model': 'model',
  'year': 'year',
  'built': 'year',
  'color': 'color',
  'colour': 'color',
  'vin': 'vin',
  'chassis': 'vin',
  'engine': 'engine_number',
  'engine number': 'engine_number',
  'odometer': 'odometer',
  'kms': 'odometer',
  'kilometers': 'odometer',
  'mileage': 'odometer',
  'email': 'customer_email',
  'owner email': 'customer_email',
  'phone': 'customer_phone',
  'owner phone': 'customer_phone',
  'mobile': 'customer_phone',
  'owner': 'customer_name',
  'owner name': 'customer_name',
  'customer': 'customer_name',
  'notes': 'notes',
  'comments': 'notes',
};

const AUTO_MAP_INVOICE = {
  'invoice': 'original_invoice_no',
  'invoice no': 'original_invoice_no',
  'invoice number': 'original_invoice_no',
  'inv no': 'original_invoice_no',
  'inv': 'original_invoice_no',
  'doc no': 'original_invoice_no',
  'document': 'original_invoice_no',
  'date': 'invoice_date',
  'invoice date': 'invoice_date',
  'inv date': 'invoice_date',
  'created': 'invoice_date',
  'rego': 'vehicle_rego',
  'registration': 'vehicle_rego',
  'vehicle': 'vehicle_rego',
  'plate': 'vehicle_rego',
  'email': 'customer_email',
  'customer email': 'customer_email',
  'phone': 'customer_phone',
  'customer phone': 'customer_phone',
  'mobile': 'customer_phone',
  'customer': 'customer_name',
  'customer name': 'customer_name',
  'name': 'customer_name',
  'subtotal': 'subtotal',
  'sub total': 'subtotal',
  'net': 'subtotal',
  'gst': 'gst',
  'tax': 'gst',
  'vat': 'gst',
  'total': 'total',
  'amount': 'total',
  'grand total': 'total',
  'invoice total': 'total',
  'discount': 'discount',
  'status': 'status',
  'paid': 'status',
  'payment status': 'status',
  'description': 'line_item_description',
  'service': 'line_item_description',
  'work': 'line_item_description',
  'work done': 'line_item_description',
  'job': 'line_item_description',
  'notes': 'notes',
  'comments': 'notes',
  'service notes': 'service_notes',
  'technician notes': 'service_notes',
  'odometer': 'odometer',
  'kms': 'odometer',
  'mileage': 'odometer',
  'payment': 'payment_method',
  'payment method': 'payment_method',
  'pay method': 'payment_method',
};

export default function DataImport() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('boyce');
  
  // Import state
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [importResult, setImportResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) {
          navigate('/login');
          return;
        }
        const userData = await response.json();
        
        if (userData.role !== 'super_admin') {
          toast.error('Super Admin access required');
          navigate('/');
          return;
        }
        
        setUser(userData);
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const isValid = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls');
      if (!isValid) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setParsedData(null);
      setColumnMapping({});
      setImportResult(null);
    }
  };

  const handleParseFile = async () => {
    if (!file) return;

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/import/parse-excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setParsedData(data);
        
        // Auto-map columns based on header names
        const autoMapSuggestions = activeTab === 'customers' 
          ? AUTO_MAP_CUSTOMER 
          : activeTab === 'vehicles' 
            ? AUTO_MAP_VEHICLE 
            : AUTO_MAP_INVOICE;
        const autoMapping = {};
        data.columns.forEach(col => {
          const normalizedCol = col.toLowerCase().trim();
          if (autoMapSuggestions[normalizedCol]) {
            autoMapping[col] = autoMapSuggestions[normalizedCol];
          } else {
            for (const [key, value] of Object.entries(autoMapSuggestions)) {
              if (normalizedCol.includes(key) || key.includes(normalizedCol)) {
                autoMapping[col] = value;
                break;
              }
            }
          }
        });
        setColumnMapping(autoMapping);
        
        toast.success(`Found ${data.total_rows.toLocaleString()} rows with ${data.columns.length} columns`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to parse Excel file');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Failed to parse Excel file');
    } finally {
      setParsing(false);
    }
  };

  const handleMappingChange = (excelColumn, systemField) => {
    setColumnMapping(prev => ({
      ...prev,
      [excelColumn]: systemField
    }));
  };

  const handleImport = async () => {
    const mappedFields = Object.values(columnMapping).filter(v => v && v !== 'skip');
    if (mappedFields.length === 0) {
      toast.error('Please map at least one column');
      return;
    }

    // Check required field for vehicles
    if (activeTab === 'vehicles' && !Object.values(columnMapping).includes('rego')) {
      toast.error('Registration Number (rego) is required for vehicle import');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_mapping', JSON.stringify(columnMapping));
      formData.append('duplicate_action', duplicateAction);

      const endpoint = activeTab === 'customers' 
        ? '/api/import/customers' 
        : activeTab === 'vehicles' 
          ? '/api/import/vehicles' 
          : '/api/import/invoices';
      const siteParams = getSiteParams();
      const url = `${BACKEND_URL}${endpoint}${siteParams}`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setShowResultDialog(true);
        toast.success(`Successfully imported ${result.imported.toLocaleString()} ${activeTab}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to import ${activeTab}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Failed to import ${activeTab}`);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedData(null);
    setColumnMapping({});
    setImportResult(null);
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    handleReset();
  };

  const currentFields = activeTab === 'customers' 
    ? CUSTOMER_FIELDS 
    : activeTab === 'vehicles' 
      ? VEHICLE_FIELDS 
      : INVOICE_FIELDS;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="data-import-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/super-admin')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-bold text-2xl sm:text-3xl text-slate-900">
                Data Import
              </h1>
              <p className="text-slate-500">
                Import customers and vehicles from Excel files
              </p>
            </div>
          </div>
        </div>

        {/* Import Type Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="boyce" className="flex items-center gap-2" data-testid="tab-boyce">
              <Database className="h-4 w-4" />
              Boyce (Auto Shop)
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Import Customers
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              Import Vehicles
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Import Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="boyce" className="space-y-6">
            <BoyceImportTab />
          </TabsContent>

          <TabsContent value="customers" className="space-y-6">
            <ImportForm
              file={file}
              parsing={parsing}
              importing={importing}
              parsedData={parsedData}
              columnMapping={columnMapping}
              duplicateAction={duplicateAction}
              currentFields={currentFields}
              onFileChange={handleFileChange}
              onParse={handleParseFile}
              onMappingChange={handleMappingChange}
              onDuplicateChange={setDuplicateAction}
              onImport={handleImport}
              onReset={handleReset}
              importType="customers"
            />
          </TabsContent>

          <TabsContent value="vehicles" className="space-y-6">
            <ImportForm
              file={file}
              parsing={parsing}
              importing={importing}
              parsedData={parsedData}
              columnMapping={columnMapping}
              duplicateAction={duplicateAction}
              currentFields={currentFields}
              onFileChange={handleFileChange}
              onParse={handleParseFile}
              onMappingChange={handleMappingChange}
              onDuplicateChange={setDuplicateAction}
              onImport={handleImport}
              onReset={handleReset}
              importType="vehicles"
            />
          </TabsContent>

          <TabsContent value="invoices" className="space-y-6">
            <ImportForm
              file={file}
              parsing={parsing}
              importing={importing}
              parsedData={parsedData}
              columnMapping={columnMapping}
              duplicateAction={duplicateAction}
              currentFields={currentFields}
              onFileChange={handleFileChange}
              onParse={handleParseFile}
              onMappingChange={handleMappingChange}
              onDuplicateChange={setDuplicateAction}
              onImport={handleImport}
              onReset={handleReset}
              importType="invoices"
            />
          </TabsContent>
        </Tabs>

        {/* Result Dialog */}
        <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Import Complete
              </DialogTitle>
              <DialogDescription>
                {activeTab === 'customers' ? 'Customer' : activeTab === 'vehicles' ? 'Vehicle' : 'Invoice'} import has been completed.
              </DialogDescription>
            </DialogHeader>
            
            {importResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{importResult.imported?.toLocaleString()}</p>
                    <p className="text-sm text-green-700">Imported</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{importResult.skipped?.toLocaleString()}</p>
                    <p className="text-sm text-yellow-700">Skipped</p>
                  </div>
                </div>
                
                {importResult.updated > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResult.updated?.toLocaleString()}</p>
                    <p className="text-sm text-blue-700">Updated</p>
                  </div>
                )}

                {importResult.linked_to_customers > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{importResult.linked_to_customers?.toLocaleString()}</p>
                    <p className="text-sm text-purple-700">Linked to Customers</p>
                  </div>
                )}

                {importResult.linked_to_vehicles > 0 && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-cyan-600">{importResult.linked_to_vehicles?.toLocaleString()}</p>
                    <p className="text-sm text-cyan-700">Linked to Vehicles</p>
                  </div>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-700 mb-2">Errors ({importResult.errors.length}):</p>
                    <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.message}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li>...and {importResult.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                <Button onClick={() => setShowResultDialog(false)} className="w-full">
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Import Form Component
function ImportForm({
  file,
  parsing,
  importing,
  parsedData,
  columnMapping,
  duplicateAction,
  currentFields,
  onFileChange,
  onParse,
  onMappingChange,
  onDuplicateChange,
  onImport,
  onReset,
  importType
}) {
  const isVehicle = importType === 'vehicles';
  const isInvoice = importType === 'invoices';
  const isCustomer = importType === 'customers';

  const getTitle = () => {
    if (isVehicle) return 'Vehicles';
    if (isInvoice) return 'Invoices';
    return 'Customers';
  };

  const getDescription = () => {
    if (isVehicle) return 'Upload vehicle data with registration numbers. Vehicles can be auto-linked to existing customers.';
    if (isInvoice) return 'Upload invoice/service history data. Invoices will auto-link to existing vehicles by rego and customers by email/phone.';
    return 'Upload customer data from your previous system (e.g., AutoBoyce Library)';
  };

  const getRequiredField = () => {
    if (isVehicle) return 'rego';
    if (isInvoice) return null; // No strict required field for invoices
    return null;
  };

  const getRequiredFieldLabel = () => {
    if (isVehicle) return 'Registration Number (rego)';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Step 1: File Upload */}
      <Card className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-lg text-slate-900">
              Import {getTitle()} from Excel
            </h2>
            <p className="text-sm text-slate-500">
              {getDescription()}
            </p>
          </div>
        </div>

        {isInvoice && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Tip:</strong> Import Customers first, then Vehicles, then Invoices for best results. 
              Invoices will automatically link to existing vehicles and customers.
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</span>
            Select Excel File
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={onFileChange}
                className="h-12"
                data-testid="excel-file-input"
              />
            </div>
            <Button
              onClick={onParse}
              disabled={!file || parsing}
              className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
              data-testid="parse-file-btn"
            >
              {parsing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {parsing ? 'Parsing...' : 'Parse File'}
            </Button>
            {(parsedData || file) && (
              <Button variant="outline" onClick={onReset} className="h-12">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            )}
          </div>

          {file && !parsedData && (
            <p className="text-sm text-slate-500">
              Selected: <span className="font-medium">{file.name}</span> ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>
      </Card>

      {/* Step 2: Column Mapping */}
      {parsedData && (
        <Card className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-4">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">2</span>
            Map Columns
          </div>

          <p className="text-sm text-slate-500 mb-4">
            Found <span className="font-semibold text-blue-600">{parsedData.total_rows.toLocaleString()}</span> rows. 
            Map Excel columns to system fields:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {parsedData.columns.map((col) => (
              <div key={col} className="space-y-1">
                <Label className="text-xs text-slate-500">Excel: "{col}"</Label>
                <Select
                  value={columnMapping[col] || 'skip'}
                  onValueChange={(value) => onMappingChange(col, value)}
                >
                  <SelectTrigger className="h-10" data-testid={`mapping-${col}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currentFields.map((field) => (
                      <SelectItem key={field.value} value={field.value}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Duplicate Handling */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Duplicate Handling ({isVehicle ? 'by Registration' : isInvoice ? 'by Invoice Number + Rego' : 'by Email/Phone'}):
            </Label>
            <Select value={duplicateAction} onValueChange={onDuplicateChange}>
              <SelectTrigger className="w-64 h-10" data-testid="duplicate-action-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip duplicates</SelectItem>
                <SelectItem value="update">Update existing record</SelectItem>
                <SelectItem value="create">Create new entry (allow duplicates)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Preview (first 5 rows):</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">#</TableHead>
                    {parsedData.columns.map((col) => (
                      <TableHead key={col} className="text-xs">
                        <div>
                          <span className="text-slate-500">{col}</span>
                          {columnMapping[col] && columnMapping[col] !== 'skip' && (
                            <div className="flex items-center gap-1 text-blue-600 mt-1">
                              <ArrowRight className="h-3 w-3" />
                              <span>{currentFields.find(f => f.value === columnMapping[col])?.label}</span>
                            </div>
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.preview.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs text-slate-500">{idx + 1}</TableCell>
                      {parsedData.columns.map((col) => (
                        <TableCell key={col} className="text-xs max-w-[150px] truncate">
                          {row[col] || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3: Import */}
      {parsedData && (
        <Card className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-4">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">3</span>
            Import {getTitle()}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">
                Ready to import <span className="font-semibold text-blue-600">{parsedData.total_rows.toLocaleString()}</span> {getTitle().toLowerCase()}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Mapped fields: {Object.values(columnMapping).filter(v => v && v !== 'skip').length} / {parsedData.columns.length}
              </p>
              {isVehicle && !Object.values(columnMapping).includes('rego') && (
                <p className="text-xs text-red-500 mt-1">
                  ⚠️ Registration Number (rego) mapping is required
                </p>
              )}
              {isInvoice && (
                <p className="text-xs text-slate-500 mt-1">
                  💡 Map vehicle rego and/or customer email for auto-linking
                </p>
              )}
            </div>
            <Button
              onClick={onImport}
              disabled={importing || Object.values(columnMapping).filter(v => v && v !== 'skip').length === 0 || (isVehicle && !Object.values(columnMapping).includes('rego'))}
              className="h-12 px-8 bg-green-600 hover:bg-green-700"
              data-testid="import-btn"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : isVehicle ? (
                <Car className="h-4 w-4 mr-2" />
              ) : isInvoice ? (
                <FileText className="h-4 w-4 mr-2" />
              ) : (
                <Users className="h-4 w-4 mr-2" />
              )}
              {importing ? 'Importing...' : `Import ${getTitle()}`}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
